import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ClientDto, ProjectDto, ProjectInput, RecurringPeriod, TeamMemberDto } from '@jordan/types';
import {
  Alert,
  Breadcrumb,
  Button,
  Checkbox,
  DataTable,
  type DataTableColumn,
  DatePicker,
  Input,
  Modal,
  PageHeader,
  Radio,
  SelectMenu,
  Spinner,
  Switch,
} from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { apiDelete, apiGet, apiPost, apiPut } from '../../lib/api';
import { billingTimeSidebar } from './billingTimeSidebar';

const COLORS = ['#8b5cf6', '#ef4444', '#f59e0b', '#10b981', '#0ea5e9', '#ec4899', '#6366f1', '#14b8a6'];
const PERIODS: { value: RecurringPeriod; label: string }[] = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'YEARLY', label: 'Yearly' },
];

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const shortDate = (ymd: string) => new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const money = (n: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(n);
  } catch {
    return `${n} ${currency}`;
  }
};

interface ProjectForm {
  name: string;
  color: string;
  clientId: string;
  startDate: string;
  endDate: string;
  recurring: boolean;
  recurringPeriod: RecurringPeriod;
  estimateEnabled: boolean;
  estimateHours: string;
  billable: boolean;
  useCustomRate: boolean;
  hourlyRate: string;
  fixedFeeEnabled: boolean;
  fixedFeeAmount: string;
  currency: string;
  memberIds: string[];
}

const emptyForm = (): ProjectForm => ({
  name: '',
  color: COLORS[0],
  clientId: '',
  startDate: todayYmd(),
  endDate: '',
  recurring: false,
  recurringPeriod: 'MONTHLY',
  estimateEnabled: false,
  estimateHours: '',
  billable: true,
  useCustomRate: false,
  hourlyRate: '',
  fixedFeeEnabled: false,
  fixedFeeAmount: '',
  currency: 'USD',
  memberIds: [],
});

const toForm = (p: ProjectDto): ProjectForm => ({
  name: p.name,
  color: p.color,
  memberIds: p.members.map((m) => m.id),
  clientId: p.clientId ?? '',
  startDate: p.startDate ?? '',
  endDate: p.endDate ?? '',
  recurring: p.recurring,
  recurringPeriod: p.recurringPeriod ?? 'MONTHLY',
  estimateEnabled: p.estimateEnabled,
  estimateHours: p.estimateHours == null ? '' : String(p.estimateHours),
  billable: p.billable,
  useCustomRate: p.useCustomRate,
  hourlyRate: p.hourlyRate == null ? '' : String(p.hourlyRate),
  fixedFeeEnabled: p.fixedFeeEnabled,
  fixedFeeAmount: p.fixedFeeAmount == null ? '' : String(p.fixedFeeAmount),
  currency: p.currency || 'USD',
});

function billableLabel(p: ProjectDto): string {
  if (!p.billable) return 'Non-billable';
  if (p.useCustomRate && p.hourlyRate != null) return `${money(p.hourlyRate, p.currency)}/h`;
  return 'Default rate';
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [projects, setProjects] = useState<ProjectDto[] | null>(null);
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [team, setTeam] = useState<TeamMemberDto[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const [editing, setEditing] = useState<ProjectDto | 'new' | null>(null);
  const [form, setForm] = useState<ProjectForm>(emptyForm());
  const [busy, setBusy] = useState(false);

  const load = () =>
    apiGet<ProjectDto[]>('/api/consulting/projects')
      .then(setProjects)
      .catch(() => setErr('Could not load projects.'));
  useEffect(() => {
    void load();
    apiGet<ClientDto[]>('/api/consulting/clients').then(setClients).catch(() => undefined);
    apiGet<TeamMemberDto[]>('/api/consulting/team').then(setTeam).catch(() => undefined);
  }, []);

  const toggleMember = (id: string) =>
    setForm((f) => ({
      ...f,
      memberIds: f.memberIds.includes(id) ? f.memberIds.filter((x) => x !== id) : [...f.memberIds, id],
    }));

  const openNew = () => {
    setEditing('new');
    setForm(emptyForm());
  };
  const openEdit = (p: ProjectDto) => {
    setEditing(p);
    setForm(toForm(p));
  };
  const close = () => setEditing(null);
  const set = <K extends keyof ProjectForm>(key: K, value: ProjectForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.name.trim()) {
      setErr('Please enter a project name.');
      return;
    }
    setBusy(true);
    setErr(null);
    const payload: ProjectInput = {
      name: form.name.trim(),
      color: form.color,
      clientId: form.clientId || null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      recurring: form.recurring,
      recurringPeriod: form.recurring ? form.recurringPeriod : null,
      estimateEnabled: form.estimateEnabled,
      estimateHours: form.estimateEnabled && form.estimateHours !== '' ? Number(form.estimateHours) : null,
      billable: form.billable,
      useCustomRate: form.useCustomRate,
      hourlyRate: form.billable && form.useCustomRate && form.hourlyRate !== '' ? Number(form.hourlyRate) : null,
      fixedFeeEnabled: form.fixedFeeEnabled,
      fixedFeeAmount: form.fixedFeeEnabled && form.fixedFeeAmount !== '' ? Number(form.fixedFeeAmount) : null,
      currency: form.currency || 'USD',
      memberIds: form.memberIds,
    };
    try {
      if (editing === 'new') await apiPost('/api/consulting/projects', payload);
      else if (editing) await apiPut(`/api/consulting/projects/${editing.id}`, payload);
      close();
      await load();
    } catch {
      setErr('Could not save the project.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p: ProjectDto) => {
    if (!window.confirm(`Delete ${p.name}? This can’t be undone.`)) return;
    try {
      await apiDelete(`/api/consulting/projects/${p.id}`);
      await load();
    } catch {
      setErr('Could not delete the project.');
    }
  };

  const columns: DataTableColumn<ProjectDto>[] = [
    {
      key: 'name',
      header: 'Project',
      sortable: true,
      sortValue: (p) => p.name.toLowerCase(),
      render: (p) => (
        <div className="d-flex align-items-center">
          <span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: p.color }} />
          <span className="fw-semibold">{p.name}</span>
        </div>
      ),
    },
    { key: 'clientName', header: 'Client', sortable: true, render: (p) => p.clientName || '—' },
    {
      key: 'timeframe',
      header: 'Timeframe',
      render: (p) =>
        p.startDate ? `${shortDate(p.startDate)}${p.endDate ? ` – ${shortDate(p.endDate)}` : ''}` : '—',
    },
    {
      key: 'recurring',
      header: 'Recurring',
      render: (p) => (p.recurring ? PERIODS.find((x) => x.value === p.recurringPeriod)?.label ?? 'Yes' : '—'),
    },
    {
      key: 'billable',
      header: 'Billable status',
      render: (p) =>
        p.billable ? (
          <span className="badge bg-primary bg-opacity-25 text-primary">
            <i className="ph-currency-dollar me-1" />
            {billableLabel(p)}
          </span>
        ) : (
          <span className="text-muted">Non-billable</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (p) => (
        <div className="d-flex justify-content-end gap-1">
          <button className="btn btn-light btn-icon btn-sm rounded-pill border-transparent" title="Edit" onClick={() => openEdit(p)}>
            <i className="ph-pencil" />
          </button>
          <button
            className="btn btn-light btn-icon btn-sm rounded-pill border-transparent text-danger"
            title="Delete"
            onClick={() => void remove(p)}
          >
            <i className="ph-trash" />
          </button>
        </div>
      ),
    },
  ];

  const clientOptions = [{ value: '', label: 'No client' }, ...clients.map((c) => ({ value: c.id, label: c.name }))];

  return (
    <AppChrome secondarySidebar={billingTimeSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Billing &amp; Time - <span className="fw-normal">Projects</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Consulting' },
              { label: 'Billing & Time' },
              { label: 'Projects' },
            ]}
          />
        }
        actions={
          <Button variant="primary" icon="ph-plus" onClick={openNew}>
            New project
          </Button>
        }
      />

      <div className="content">
        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {projects === null ? (
          <Spinner />
        ) : projects.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="ph-folders d-block fs-1 text-muted mb-2" />
              <h5 className="mb-1">No projects yet</h5>
              <p className="text-muted mb-3">Create a project to track time and billing against a client.</p>
              <Button variant="primary" icon="ph-plus" onClick={openNew}>
                New project
              </Button>
            </div>
          </div>
        ) : (
          <div className="card border-0 shadow-sm">
            <DataTable
              columns={columns}
              rows={projects}
              getRowKey={(p) => p.id}
              searchable
              searchPlaceholder="Search projects…"
              initialSort={{ key: 'name', dir: 'asc' }}
              pageSize={20}
            />
          </div>
        )}
      </div>

      {editing && (
        <Modal
          open
          size="lg"
          onClose={close}
          title={editing === 'new' ? 'Create new project' : `Edit ${(editing as ProjectDto).name}`}
          footer={
            <>
              <Button variant="light" onClick={close}>
                Cancel
              </Button>
              <Button variant="primary" disabled={busy} onClick={() => void save()}>
                {editing === 'new' ? 'Create project' : 'Save changes'}
              </Button>
            </>
          }
        >
          {/* Name + colour */}
          <div className="d-flex align-items-center gap-2 mb-2">
            <div className="dropdown">
              <span
                className="d-inline-block rounded-circle"
                style={{ width: 22, height: 22, background: form.color }}
                title="Project colour"
              />
            </div>
            <div className="flex-grow-1">
              <Input className="mb-0" placeholder="Project name" value={form.name} onChange={(e) => set('name', e.target.value)} />
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mb-3">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => set('color', c)}
                title={c}
                className="rounded-circle border-0"
                style={{ width: 22, height: 22, background: c, outline: form.color === c ? '2px solid var(--body-color)' : 'none', outlineOffset: 2 }}
              />
            ))}
          </div>

          <div className="mb-3" style={{ maxWidth: 360 }}>
            <SelectMenu label="Client" icon="ph-identification-card" value={form.clientId} onChange={(v) => set('clientId', v)} options={clientOptions} />
          </div>

          {team.length > 0 && (
            <div className="mb-3">
              <label className="form-label">Team</label>
              <div className="d-flex flex-wrap gap-3">
                {team.map((m) => (
                  <Checkbox
                    key={m.id}
                    label={m.name}
                    checked={form.memberIds.includes(m.id)}
                    onChange={() => toggleMember(m.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Timeframe */}
          <h6 className="text-muted text-uppercase fs-sm">Timeframe</h6>
          <div className="row">
            <div className="col-sm-6">
              <DatePicker label="Start date" value={form.startDate} onChange={(v) => set('startDate', v)} />
            </div>
            <div className="col-sm-6">
              <DatePicker label="End date" value={form.endDate} onChange={(v) => set('endDate', v)} placeholder="No end date" />
            </div>
          </div>

          {/* Recurring */}
          <div className="border-top pt-3 mt-1">
            <Switch label="Recurring" checked={form.recurring} onChange={(e) => set('recurring', e.target.checked)} />
            {form.recurring && (
              <div className="mt-2" style={{ maxWidth: 240 }}>
                <SelectMenu
                  label="Period"
                  icon="ph-repeat"
                  value={form.recurringPeriod}
                  onChange={(v) => set('recurringPeriod', v as RecurringPeriod)}
                  options={PERIODS}
                />
              </div>
            )}
          </div>

          {/* Time estimate */}
          <div className="border-top pt-3 mt-3">
            <Switch label="Time estimate" checked={form.estimateEnabled} onChange={(e) => set('estimateEnabled', e.target.checked)} />
            {form.estimateEnabled && (
              <div className="mt-2" style={{ maxWidth: 240 }}>
                <Input
                  label="Estimate (hours)"
                  type="number"
                  min={0}
                  step="0.5"
                  value={form.estimateHours}
                  onChange={(e) => set('estimateHours', e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Billable */}
          <div className="border-top pt-3 mt-3">
            <Switch label="Billable" checked={form.billable} onChange={(e) => set('billable', e.target.checked)} />
            {form.billable && (
              <div className="mt-2">
                <Radio
                  name="rate"
                  label="Default hourly rate (from the client)"
                  checked={!form.useCustomRate}
                  onChange={() => set('useCustomRate', false)}
                />
                <Radio
                  name="rate"
                  label="Custom project hourly rate"
                  checked={form.useCustomRate}
                  onChange={() => set('useCustomRate', true)}
                />
                {form.useCustomRate && (
                  <div className="row mt-2">
                    <div className="col-sm-4">
                      <Input
                        label="Hourly rate"
                        type="number"
                        min={0}
                        step="0.01"
                        value={form.hourlyRate}
                        onChange={(e) => set('hourlyRate', e.target.value)}
                      />
                    </div>
                    <div className="col-sm-3">
                      <Input label="Currency" value={form.currency} onChange={(e) => set('currency', e.target.value)} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Fixed fee */}
          <div className="border-top pt-3 mt-3">
            <Switch label="Fixed fee" checked={form.fixedFeeEnabled} onChange={(e) => set('fixedFeeEnabled', e.target.checked)} />
            {form.fixedFeeEnabled && (
              <div className="row mt-2">
                <div className="col-sm-4">
                  <Input
                    label="Amount"
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.fixedFeeAmount}
                    onChange={(e) => set('fixedFeeAmount', e.target.value)}
                  />
                </div>
                <div className="col-sm-3">
                  <Input label="Currency" value={form.currency} onChange={(e) => set('currency', e.target.value)} />
                </div>
              </div>
            )}
          </div>

          <div className="text-muted fs-sm mt-3">
            A project’s custom rate overrides the client’s default rate for billing.
          </div>
        </Modal>
      )}
    </AppChrome>
  );
}
